import React, { useEffect, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect, useSelector } from 'react-redux';
import type { Location } from 'history';

import MarketplaceAwareLink from 'components/common/MarketplaceAwareLink';
import ProductName from 'components/productdetail/ProductName';
import BuyBoxReviewBlurb from 'components/productdetail/BuyBoxReviewBlurb';
import { CUSTOMER_DROPDOWN_DIMENSION_SELECTION } from 'constants/productDimensionSelectionSourceTypes';
import { stockSelectionCompleted } from 'store/ducks/productDetail/actions';
import { ProductNotifyModal } from 'components/productdetail/asyncProductPageModals';
import { toggleProductNotifyModal } from 'actions/productdetail/sharing';
import Price from 'components/productdetail/Price';
import { SmallLoader } from 'components/Loader';
import ProductUtils from 'helpers/ProductUtils';
import useMartyContext from 'hooks/useMartyContext';
import useProduct from 'hooks/useProduct';
import shippingBox from 'images/shipping-box.svg';
import { translateCartError } from 'apis/mafia';
import { changeQuantity } from 'actions/cart';
import { evAddToCart } from 'events/cart';
import type { ProductBundleOptions, ProductImage, ProductStockData, ProductStyle } from 'types/cloudCatalog';
import { heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import type { SelectedSizing } from 'types/product';
import type { CartError, CartResponse } from 'types/mafia';
import usePrevious from 'hooks/usePrevious';
import Carousel from 'components/common/Carousel/Carousel';
import { constructMSAImageUrl } from 'helpers/index';
import { ProductCardState } from 'types/influencer';
import { SOLD_LAST_ITEM_IN_THIS_SIZE, YOU_JUST_MISSED_IT } from 'constants/influencerMessages';
import StylePicker from 'components/productdetail/stylepicker/StylePicker';
import Hearts from 'components/common/Hearts';
import type { AppState } from 'types/app';

import css from 'styles/components/outfitRecos/recosProductDetail.scss';
// eslint-disable-next-line css-modules/no-unused-class
import productCardCSS from 'styles/components/influencer/shoppablePosts/customerViewProductCard.scss';

interface OwnProps {
  id: string;
  merchantId?: string;
  initialStyleId: string;
  pageType: string;
  productQuery: ProductBundleOptions;
  onViewDetailsClick?: any;
}

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = OwnProps & PropsFromRedux;

export const RecosProductDetail = (props: Props) => {
  const {
    productQuery,
    initialStyleId,
    merchantId,
    changeQuantity,
    stockSelectionCompleted,
    toggleProductNotifyModal,
    toggleHeartingLoginModal,
    heartProduct,
    unHeartProduct,
    pageType,
    id,
    onViewDetailsClick
  } = props;
  const {
    testId,
    marketplace: { pdp: pdpConfig },
    trackLegacyEvent,
    amethystTrack
  } = useMartyContext();

  // keep track of the previous requested styleId as the InlineRecoProduct component controls changing the product externally
  // To prevent cases where wrong style is shown while in the process of loading a new product entirely
  const previousInitialStyleId = usePrevious(initialStyleId);

  const [product, setProduct] = useProduct(productQuery, merchantId);

  const [showOutOfStockModal, setShowOutOfStockModal] = useState<boolean>(false);
  const [formError, setFormError] = useState<boolean>(false);
  const [userAttemptedProductAction, setUserAttemptedProductAction] = useState<boolean>(false);
  const [productCardState, setProductCardState] = useState<ProductCardState>(ProductCardState.SIZE_PICK);
  const cookies = useSelector((state: AppState) => state.cookies);

  const isCustomer = !!cookies['x-main'];
  const styleId = product?.styleId || initialStyleId;

  useEffect(() => {
    setProductCardState(ProductCardState.SIZE_PICK);
  }, [styleId]);

  if (!product) {
    return (
      <div className={css.placeholder}>
        <SmallLoader />
      </div>
    );
  }

  const { colorId, detail, selectedSizing, styleThumbnails, validation } = product;
  const { defaultProductType, defaultImageUrl, brandName, productName, genders, oos, productId, sizing, styles, reviewCount, productRating } =
    detail;
  const productStyles = ProductUtils.getStyleMap(styles);

  const style = productStyles[styleId] || (previousInitialStyleId && productStyles[previousInitialStyleId]) || styles[0]!;
  const { images, productUrl } = style;
  const stock = ProductUtils.getStockBySize(sizing.stockData, style.colorId, selectedSizing);

  const showFormError = () => {
    if (!ProductUtils.isSizeSelectionComplete(sizing, selectedSizing)) {
      setFormError(true);
    }
  };
  const hideFormError = () => {
    if (!userAttemptedProductAction) {
      setFormError(false);
    }
  };

  const onHeartClick = ({ styleId, productId }: { styleId: string; productId: string }, isHearted: boolean) => {
    if (isCustomer) {
      const fn = isHearted ? unHeartProduct : heartProduct;
      // @ts-ignore
      fn({ itemId: styleId, productId, colorId });
    } else {
      toggleHeartingLoginModal(true, styleId);
    }
  };

  const heartsData = {
    showFavoriteHeart: true,
    onHeartClick,
    isDisplayCount: false
  };

  const onAddToCart = (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUserAttemptedProductAction(true);
    if (stock) {
      if (ProductUtils.isSizeSelectionComplete(sizing, selectedSizing)) {
        addToCartCall(stock, style);
      } else {
        setFormError(true);
      }
    } else {
      setShowOutOfStockModal(true);
    }
  };

  const addToCartCall = (stock: ProductStockData, style: ProductStyle) => {
    setProductCardState(ProductCardState.ADD_TO_CART_IN_PROGRESS);
    const amethystEventBundle = { ...style, addedFrom: pageType };
    amethystTrack(() => [evAddToCart, amethystEventBundle]);
    trackLegacyEvent('CartAddItem', null, `stockId:${stock.id}|styleId:${style.styleId}`);
    changeQuantity({ items: [{ stockId: stock.id, quantity: 1, merchantId, quantityAddition: true }] }, { firePixel: true }).then(
      (response: CartResponse | CartError) => {
        const error = translateCartError(response);
        if (error) {
          setProductCardState(ProductCardState.ADD_TO_CART_FAIL);
          alert(error);
        } else {
          setProductCardState(ProductCardState.ADD_TO_CART_SUCCESS);
        }
        setTimeout(() => setProductCardState(ProductCardState.SIZE_PICK), 5000);
      }
    );
  };

  const onModalStockChange = (styleId: string, selectedSizing?: SelectedSizing) => {
    const newSizing = { ...product.selectedSizing, ...selectedSizing };
    const style = productStyles[styleId]!;

    setProduct({
      ...product,
      styleId,
      colorId: style.colorId,
      selectedSizing: newSizing,
      validation: {
        dimensions: sizing.dimensionsSet.reduce(
          (newDimension, dimensionId) => ({
            ...newDimension,
            [dimensionId]: selectedSizing && !selectedSizing[dimensionId]
          }),
          {}
        )
      }
    });

    if (ProductUtils.isSizeSelectionComplete(sizing, newSizing)) {
      stockSelectionCompleted({
        source: CUSTOMER_DROPDOWN_DIMENSION_SELECTION,
        product: { ...product, selectedSizing: newSizing }
      });

      const newStock = ProductUtils.getStockBySize(sizing.stockData, style.colorId, newSizing);
      if (!newStock) {
        setShowOutOfStockModal(true);
      }
    }
  };

  const onProductNotifyClose = () => {
    setShowOutOfStockModal(false);
    toggleProductNotifyModal(false);
  };

  const makePDPLink = () => {
    const detailsLink = productUrl && (
      <MarketplaceAwareLink
        merchantId={merchantId}
        className={css.fullDetailsLink}
        onClick={onViewDetailsClick}
        to={productUrl}
        data-test-id={testId('recosQuickViewDetails')}
      >
        View Details
      </MarketplaceAwareLink>
    );
    return detailsLink;
  };

  const makeCarouselSlide = (image: ProductImage) => {
    const { imageId, type } = image;

    return <img className={css.galleryImage} src={constructMSAImageUrl(imageId, { autoCrop: true })} alt={type} />;
  };

  const addToCartFailedMarkup = (
    <div className={productCardCSS.atcFailedContainer} aria-live="polite">
      <div className={productCardCSS.atcFailedHeaderContainer}>
        <span className={productCardCSS.atcFailedIcon} aria-hidden={true} />
        <span className={productCardCSS.atcFailedHeader}>{YOU_JUST_MISSED_IT}</span>
      </div>
      <span className={productCardCSS.atcFailedMessage}>{SOLD_LAST_ITEM_IN_THIS_SIZE}</span>
    </div>
  );

  const addToCartSuccessMarkup = (
    <div className={productCardCSS.addToCartSuccess} aria-live="polite">
      <span className={productCardCSS.check} />
      Added To Bag
    </div>
  );

  return (
    <div className={css.container}>
      <div className={css.productGallery}>
        <Hearts {...heartsData} styleId={styleId} productId={productId} />
        <Carousel
          slides={images.map(image => makeCarouselSlide(image)).map(slide => ({ ...slide, key: slide.props.alt })) as JSX.Element[]}
          slideWidths={{ mobile: '100%', tablet: '100%', desktop: '100%' }}
        />
      </div>
      <div className={css.productInfo}>
        <ProductName showSocialLinks={true} colorId={colorId} product={detail} />

        <Price
          className={css.priceContainer}
          productStyle={style}
          percentOffText={pdpConfig.percentOffText}
          showPercentOffBanner={pdpConfig.showPercentOffBanner}
        />
        <div className={css.reviewContainer}>
          <div className={css.shipsFree} data-test-id={testId('shipsFreeImg')}>
            <img src={shippingBox} alt="Ships Free" />
            SHIPS FREE
          </div>
          <BuyBoxReviewBlurb numReviews={reviewCount} rating={productRating} isReviewClickable={false} />
        </div>
        <div data-test-id={testId('buyBoxContainer')}>
          {oos ? (
            <div className={css.oosText}>Sorry! This product is unavailable.</div>
          ) : (
            styleThumbnails && (
              <StylePicker
                id={id}
                showOosNotifyMe={true}
                styleList={styles}
                product={product as any}
                productId={productId}
                productType={defaultProductType}
                productImage={defaultImageUrl}
                productTitle={`${brandName} ${productName}`}
                sizing={sizing}
                genders={genders}
                selectedSizing={selectedSizing}
                selectedStyle={style}
                thumbnails={styleThumbnails}
                dimensionValidation={validation.dimensions}
                onStockChange={onModalStockChange}
                onAddToCart={onAddToCart}
                addToCartAction={pdpConfig.addToCartAction}
                hydraBelowAddToCartSlot={false}
                isGiftCard={ProductUtils.isGiftCard(product?.detail?.defaultProductType)}
                isSelectSizeTooltipVisible={formError}
                isSelectSizeTooltipHighlighted={formError}
                onOpenProductNotifyMe={() => {}}
                onShowSelectSizeTooltip={showFormError}
                onHideSelectSizeTooltip={hideFormError}
                onUnhighlightSelectSizeTooltip={hideFormError}
                showSizeGender={pdpConfig.showSizeGender}
                showSizeChartLink={pdpConfig.showSizeChartLink}
                hasRecommendedSizing={false}
                pageType={pageType}
                showAddToCart={productCardState === ProductCardState.SIZE_PICK}
                useDropdowns={false}
                useStickyAddToCart={false}
                allowTwoDayShippingPrimePerk={true}
                hidePerks={productCardState !== ProductCardState.SIZE_PICK}
                location={window.location as unknown as Location}
                showSymphonySizeBuyBox={false}
              />
            )
          )}
          {
            {
              [ProductCardState.SIZE_PICK]: <></>,
              [ProductCardState.ADD_TO_CART_FAIL]: addToCartFailedMarkup,
              [ProductCardState.ADD_TO_CART_SUCCESS]: addToCartSuccessMarkup,
              [ProductCardState.ADD_TO_CART_IN_PROGRESS]: <SmallLoader />
            }[productCardState]
          }
          {makePDPLink()}
        </div>
        {showOutOfStockModal && (
          <ProductNotifyModal
            isOpen={showOutOfStockModal}
            onClose={onProductNotifyClose}
            productId={productId}
            colorId={style.colorId}
            selectedSizing={selectedSizing}
          />
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  changeQuantity,
  stockSelectionCompleted,
  toggleProductNotifyModal,
  toggleHeartingLoginModal,
  heartProduct,
  unHeartProduct
};

const connector = connect(null, mapDispatchToProps);

export default connector(RecosProductDetail);
